/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Shipping from '@mui/icons-material/LocalShippingOutlined';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useBillingHeaderProvider } from 'mf_core/provider';
import { BillingModeSelectorContent } from './BillingModeSelectorContent';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { BillingHeaderContextTo } from 'mf_core/provider/billing-header/billing-header-context';
import {
  businessCheckIcon,
  businessIcon,
  CustomerCheckIcon,
  CustomerIcon,
} from './BillingModeSelectorIcons';
import Stack from '@mui/material/Stack';
import { ResellerBillingSelector } from '../../../app/component/ResellerBillingSelector/ResellerBillingSelector';
import { useResellerProfile } from 'mf_core/auth';
import { useTranslate } from '@im/react-localization';
import ClickAwayListener from '@mui/material/ClickAwayListener';

export function BillingModeSelector() {
  const translate = useTranslate();
  const { data: resellerData } = useResellerProfile(true);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isMyBusiness, setIsMyBusiness] = useState(true);
  const { billingHeader, modifyBillingHeader } = useBillingHeaderProvider();
  const [defaultAddress, setDefaultAddress] = useState<any>({});
  const storageBilling = localStorage.getItem('billingAddress')
    ? JSON.parse(localStorage.getItem('billingAddress') ?? '')
    : [];
  const resellerType = resellerData?.resellerType?.toLowerCase();
  const [customerPopOpen, setCustomerPopOpen] = useState(
    !storageBilling?.cnpjNumber
  );

  const getFormattedAddress = (address: any) => {
    let FormatAddress = [
      address?.line1,
      address?.line2,
      address?.line3,
      address?.line4,
      address?.postalCode,
      address?.city,
    ];
    FormatAddress = FormatAddress?.map((x) => (x ? x?.trimEnd() : x))?.filter(
      (x) => x !== '' && x !== null
    );
    return FormatAddress.join(', ');
  };
  useEffect(() => {
    if (resellerData) {
      let billaddress = [];
      if (resellerType === 'hasie') {
        billaddress = resellerData?.billToAddresses?.filter(
          (x: any) => x.imAddressId === '000'
        );
        setDefaultAddress(billaddress[0]);
      }
      if (!billingHeader?.cnpjNumber && Boolean(storageBilling?.cnpjNumber)) {
        modifyBillingHeader(storageBilling);
        setIsMyBusiness(
          resellerType === 'hasie'
            ? storageBilling?.selectedMode === 'business'
            : false
        );
      } else if (resellerType === 'hasie') {
        if (billaddress) updateBillingHeader(billaddress[0], true);
        setIsMyBusiness(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerData]);

  const updateBillingHeader = (address: any, business: boolean) => {
    const newObj: BillingHeaderContextTo = {
      billingAddress: [address],
      cnpjNumber: address?.taxExemptNumber,
      hasIE: business ? resellerData?.hasIE : address?.hasIE,
      resellerId: business ? resellerData?.id : address.resellerNumber,
      resellerType: resellerData?.resellerType,
      selectedMode: business ? 'business' : 'customer',
      shipToProvince: address?.city,
      shipToUF: address?.state,
    };
    modifyBillingHeader({ ...newObj });
  };

  const primaryOnClick = (myBusiness: boolean, selectedAddress: any) => {
    setPopoverOpen(false);
    updateBillingHeader(selectedAddress, myBusiness);
  };

  const onCancelClick = () => {
    setPopoverOpen(false);
    setIsMyBusiness(billingHeader?.selectedMode === 'business');
  };

  const tootipTitle = () => {
    return (
      <Stack spacing={1}>
        <TypographyTooltip
          sx={{
            color: 'var(--color-gray-700, #364152)',
            fontWeight: 600,
          }}
        >{`${
          billingHeader?.selectedMode === 'customer'
            ? translate(`BillingHeader.Customer`)
            : translate(`BillingHeader.Reseller`)
        }:${billingHeader?.cnpjNumber ?? ''}`}</TypographyTooltip>
        <TypographyTooltip
          sx={{
            color: 'var(--color-gray-500, #697586)',
            fontWeight: 500,
          }}
        >
          {getFormattedAddress(billingHeader?.billingAddress?.[0])}
        </TypographyTooltip>
      </Stack>
    );
  };

  return resellerData &&
    (resellerType === 'hasie' ||
      resellerType === 'hasnotie' ||
      resellerType === 'hasiewithexempt') ? (
    <Box margin={'0px 32px'}>
      {(resellerType === 'hasnotie' || resellerType === 'hasiewithexempt') && (
        <ResellerBillingSelector
          popoverOpen={customerPopOpen}
          resellerType={resellerType}
          setPopoverOpen={setCustomerPopOpen}
        />
      )}
      <Tooltip title={tootipTitle()}>
        <StyledButton
          data-name={`billing_${billingHeader?.selectedMode}`}
          disableRipple={true}
          onClick={() => {
            if (!billingHeader?.disableHeader) {
              if (resellerType === 'hasie') setPopoverOpen(true);
              else setCustomerPopOpen(true);
            }
          }}
          startIcon={<Shipping color="primary" />}
        >
          <TypographySelector variant="smalltextsemibold">
            {`${
              billingHeader?.selectedMode === 'customer'
                ? translate(`BillingHeader.Customer`)
                : translate(`BillingHeader.Reseller`)
            }: ${billingHeader?.cnpjNumber ?? ''}`}
          </TypographySelector>
        </StyledButton>
      </Tooltip>

      <StylePopover open={popoverOpen}>
        <ClickAwayListener onClickAway={onCancelClick}>
          <Stack>
            <Box
              borderBottom="1px solid var(--Gray-200, #E3E8EF)"
              padding="16px 12px"
            >
              <Typography variant="text-sm-semibold">
                {translate('BillingHeader.WhoAreYouShoppingForToday')}
              </Typography>
            </Box>
            <Box
              key="header"
              sx={{
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                padding: '20px 16px 8px 16px',
              }}
            >
              <TableRow
                sx={{
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  display: 'flex',
                  gap: '16px',
                }}
              >
                <HeaderTableCell
                  key="myBusiness"
                  style={{
                    background: isMyBusiness ? Primary50 : Gray25,
                    border: isMyBusiness ? BorderPrimary700 : BorderGray200,
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setIsMyBusiness(true);
                    }}
                  >
                    {isMyBusiness ? businessCheckIcon : businessIcon}
                  </IconButton>
                  {translate(`BillingHeader.MyBusiness`)}
                </HeaderTableCell>
                <HeaderTableCell
                  key="MyCustomer"
                  style={{
                    background: !isMyBusiness ? Primary50 : Gray25,
                    border: !isMyBusiness ? BorderPrimary700 : BorderGray200,
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setIsMyBusiness(false);
                    }}
                  >
                    {!isMyBusiness ? CustomerCheckIcon : CustomerIcon}
                  </IconButton>
                  {translate(`BillingHeader.MyCustomer`)}
                </HeaderTableCell>
              </TableRow>
            </Box>
            <Box>
              <BillingModeSelectorContent
                defaultAddress={defaultAddress}
                getFormattedAddress={getFormattedAddress}
                isMyBusiness={isMyBusiness}
                onCancelClick={onCancelClick}
                onPrimaryClick={primaryOnClick}
                shipToAddresses={resellerData?.billToAddresses}
              />
            </Box>
          </Stack>
        </ClickAwayListener>
      </StylePopover>
    </Box>
  ) : (
    <></>
  );
}

const StyledButton = styled(Button)(({ theme }) => ({
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    backgroundColor: 'white',
  },
  color: theme.palette.info.main,
  display: 'flex',
  fontSize: '14px',
  fontWeight: 600,
  height: '32px',
  lineHeight: '22px',
  padding: '5px 12px',
}));

const StylePopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    boxShadow: 'none',
  },
  '& .MuiPopover-paper': {
    border: '1px solid var(--Gray-200, #E3E8EF)',
    borderRadius: 8,
    boxShadow:
      '0px 12px 16px -4px rgba(18, 25, 38, 0.08), 0px 4px 6px -2px rgba(18, 25, 38, 0.03)',
    left: 'calc(100% - 80%) !important',
    overflow: 'hidden',
    right: '18px !important',
    top: '42px !important',
    width: '360px',
  },
}));

const TypographySelector = styled(Typography)(() => ({
  fontFamily: 'Roboto',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '22px',
}));
const HeaderTableCell = styled(TableCell)(() => ({
  alignItems: 'center',
  borderRadius: 'var(--radius-md, 8px)',
  boxShadow:
    'var(--shadow-sm-1-x, 0px) var(--shadow-sm-1-y, 1px) var(--shadow-sm-1-blur, 3px) var(--shadow-sm-1-spread, 0px) rgba(18, 25, 38, 0.10), var(--shadow-sm-2-x, 0px) var(--shadow-sm-2-y, 1px) var(--shadow-sm-2-blur, 2px) var(--shadow-sm-2-spread, 0px) rgba(18, 25, 38, 0.06)',
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  gap: '4px',
  justifyContent: 'center',
  padding: '20px 14px 8px 14px',
}));

const IconButton = styled(Button)(({ theme }) => ({
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    background: 'none',
  },
  alignItems: 'center',
  border: '0px',
  display: 'flex',
  gap: '8px',
  height: '46px',
  justifyContent: 'center',
  padding: '8px',
  width: '46px',
}));

const TypographyTooltip = styled(Typography)(() => ({
  fontFamily: 'Inter',
  fontSize: '12px',
  fontStyle: 'normal',
  lineHeight: '18px',
}));

const Primary50 = 'var(--color-primary-50, #EFF8FF)';
const Gray25 = 'var(--color-gray-25, #FCFCFD)';
const BorderPrimary700 = '2px solid var(--color-primary-700, #175CD3)';
const BorderGray200 = '1px solid var(--color-gray-200, #D4DCE7)';
